import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { session, loadCapabilities } from './lib/session.ts';

/**
 * Route guards ask the API, never a token claim.
 *
 * `requires` names a capability the server told us about. Hiding a route is a
 * convenience for the person using it; the API returns 403 either way, which is
 * what the build brief requires — "another panel returns 403 from the API, not
 * just a hidden link".
 */
export type Capability = 'judge' | 'conductor' | 'team';

declare module 'vue-router' {
  interface RouteMeta {
    requires?: Capability;
    public?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  { path: '/', component: () => import('./views/Home.vue'), meta: { public: true } },
  { path: '/callback', component: () => import('./views/Callback.vue'), meta: { public: true } },
  /* Public: somebody has to be able to read what they are agreeing to before
     they have an account to agree with. */
  { path: '/terms', component: () => import('./views/Terms.vue'), meta: { public: true } },
  /* Public, and emphatically so: the people most likely to need it are the
     ones who cannot get past signing in. */
  { path: '/help', component: () => import('./views/Help.vue'), meta: { public: true } },
  {
    path: '/my/applications',
    component: () => import('./views/MyApplications.vue'),
  },
  {
    path: '/apply/:auditionId/:sectionId',
    component: () => import('./views/Apply.vue'),
  },
  /* Changing an application already sent, which is allowed until the close. */
  {
    path: '/my/applications/:id/revise',
    component: () => import('./views/Revise.vue'),
  },
  {
    path: '/judge',
    component: () => import('./views/JudgeDashboard.vue'),
    meta: { requires: 'judge' },
  },
  {
    path: '/judge/:panelId/:sectionId',
    component: () => import('./views/JudgeGrid.vue'),
    meta: { requires: 'judge' },
  },
  {
    path: '/judge/:panelId/:sectionId/:applicationId',
    component: () => import('./views/JudgeApplicant.vue'),
    meta: { requires: 'judge' },
  },
  {
    path: '/review',
    component: () => import('./views/ConductorReview.vue'),
    meta: { requires: 'conductor' },
  },
  {
    path: '/review/:sectionId',
    component: () => import('./views/ConductorSection.vue'),
    meta: { requires: 'conductor' },
  },
  {
    path: '/team/applicants',
    component: () => import('./views/TeamApplicants.vue'),
  },
  {
    path: '/team/videos',
    component: () => import('./views/TeamVideos.vue'),
  },
  {
    path: '/team/mail',
    component: () => import('./views/TeamMail.vue'),
  },
  {
    path: '/team/dashboard',
    component: () => import('./views/TeamDashboard.vue'),
  },
  {
    path: '/team/setup',
    component: () => import('./views/TeamSetup.vue'),
    meta: { requires: 'team' },
  },
  {
    path: '/team/adjudicators',
    component: () => import('./views/TeamAdjudicators.vue'),
    meta: { requires: 'team' },
  },
  {
    path: '/team/wording',
    component: () => import('./views/TeamWording.vue'),
    meta: { requires: 'team' },
  },
  {
    path: '/team/principals',
    component: () => import('./views/TeamPrincipals.vue'),
    meta: { requires: 'team' },
  },
  {
    path: '/team/release',
    component: () => import('./views/TeamRelease.vue'),
    meta: { requires: 'team' },
  },
  {
    path: '/team/master',
    component: () => import('./views/MasterSheet.vue'),
    meta: { requires: 'team' },
  },
  { path: '/:pathMatch(.*)*', component: () => import('./views/NotFound.vue'), meta: { public: true } },
];

export function has(capability: Capability): boolean {
  const c = session.capabilities;
  switch (capability) {
    case 'judge': return c.judgePanelIds.length > 0;
    case 'conductor': return c.isConductor;
    case 'team': return c.isTeam;
  }
}

export function createAppRouter() {
  const router = createRouter({ history: createWebHistory(), routes });

  /*
   * The guard enforces capabilities, not sign-in.
   *
   * On a fresh load of a protected URL this runs before anything has mounted,
   * so it cannot know whether anyone is signed in - and every attempt to make
   * it find out produced a worse bug than the one before. Sign-in is handled in
   * App.vue, which redirects once the answer exists. The API is the actual
   * control either way: it returns 401 or 403 regardless of what the browser
   * decided to render.
   */
  router.beforeEach(async (to) => {
    if (to.meta.public) return true;
    if (!to.meta.requires) return true;

    // A capability check needs capabilities, which need a signed-in account.
    // If nobody is signed in yet, App.vue will redirect; let the route through
    // rather than guessing.
    if (!session.authReady) return true;
    if (!session.isAuthenticated) return true;

    if (!session.loaded) await loadCapabilities();
    if (!has(to.meta.requires)) {
      // Not a redirect to an error page: the route simply is not theirs, and
      // saying so plainly beats bouncing them somewhere unexpected.
      return { path: '/not-found' };
    }
    return true;
  });

  return router;
}
